<template>
  <div
    :class="[
      'comp-button',
      buttonSize,
      {
        'is-loading': props.loading,
        'is-disabled': props.disabled,
        'is-circle': props.circle,
        [`comp-button--${props.type}`]: props.type,
      },
    ]"
    :style="style"
    @click="handleClick"
  >
    <comp-svg v-if="isSvgIcon" :name="icon" :style="prefixIconStyle"></comp-svg>
    <i v-else="icon" :style="prefixIconStyle" :class="[icon, 'comp-button__icon']"></i>
    <slot></slot>
    <i v-if="appendIcon" :class="[appendIcon, 'comp-button__icon', 'append-icon']" :style="appendStyle"></i>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  isSvgIcon: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: String,
    default: '',
  },
  appendIcon: {
    type: String,
    default: '',
  },
  size: {
    type: String,
    validator: (value) => ['large', 'medium', 'small', 'mini'].includes(value),
    default: 'medium',
  },
  type: {
    type: String,
    validator: (value) =>
      [
        'normal',
        'default',
        'primary',
        'success',
        'warning',
        'danger',
        'info',
        'text',
        'delete', // 批量删除按钮
        'plain', // 
      ].includes(value),
    default: 'default',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  circle: {
    type: Boolean,
    default: false,
  },
  style: {
    type: Object,
    default: {}
  },
  prefixIconStyle: {
    type: Object,
    default: {}
  },
  appendStyle: {
    type: Object,
    default: {}
  }
});

const emit = defineEmits(['click']);

const buttonSize = computed(() => {
  return props.size ? `comp-button--${props.size}` : '';
});

function handleClick(event) {
  if (props.loading || props.disabled) {
    event.preventDefault();
    return;
  }
  emit('click', event);
}
</script>

<style lang="scss" scoped>
.comp-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-sizing: border-box;
  white-space: nowrap;
  outline: none;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  // background: linear-gradient(180deg, rgba(243, 245, 251, 0) 0%, #F3F5FB 100%);
  background: rgba(38, 152, 252, 0.1);
  text-align: center;
  color: #2684FC;
  font-size: 13px;
  font-weight: 400;
  border-radius: 4px;
  border: solid 1px rgba(38, 152, 252, 0.1);
  font-family: Roboto-Regular;
  cursor: pointer;
  width: max-content;
  /* 选择所有 .comp-button 元素，但排除作为最后一个子元素的 .comp-button */
  &:not(:last-child) {
    margin-right: 8px;
  }
  // .avue-crud__menu { // 表格操作菜单下的按钮纯白
  //   .comp-button {
  //     background: #ffffff;
  //   }
  // }
}
/* Add styles for different types and sizes */
.comp-button.is-loading::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 1;
}

.comp-button.is-disabled {
  cursor: not-allowed;
  color: #C4CBD3;
  background-color: #F3F5F7;
  border-color: #DADFE4;
}

.comp-button.is-circle {
  border-radius: 50%;
}


/* Sizes */
.comp-button--large {
  max-height: 40px;
  padding: 9px 9px;
  font-size: 16px;
  .comp-button__icon {
    font-size: 18px;
    margin-right: 4px;
  }
  .icon-Search {
    font-size: 15px;
    padding-right: 1px;
  }
}

.comp-button--medium {
  max-height: 32px;
  padding: 8px 16px;
  font-size: 14px;
  .comp-button__icon {
    font-size: 16px;
    margin-right: 3px;
  }
  .icon-Search {
    font-size: 13.5px;
    padding-right: 2px;
  }
}

.comp-button--small {
  max-height: 28px;
  padding: 4px 5px;
  font-size: 12px;
  .comp-button__icon {
    font-size: 14px;
    margin-right: 2px;
  }
  .icon-Search {
    font-size: 13px;
    padding-right: 2px;
  }
}

.comp-button--mini {
  max-height: 24px;
  padding: 2px 4px;
  font-size: 10px;
  .comp-button__icon {
    font-size: 12px;
    margin-right: 1px;
  }
  .icon-Search {
    font-size: 11px;
    padding-right: 2px;
  }
}
.append-icon {
  margin-left: 5px;
  font-size: inherit !important;
}
/* Types */
.comp-button--normal {
  background: #ffffff;
  &:hover {
    color: #2684FC;
    background-color: #ecf5ff;
    border: solid 1px #c6e2ff;
  }
}
.comp-button--default {
  &:hover {
    color: #2684FC;
    background-color: #ecf5ff;
    border: solid 1px #c6e2ff;
  }
}
.comp-button--primary {
  color: #fff;
  background: #2684FC;
  border-color: #2684FC;
  // &:not(.is-text) {
  //   box-shadow: inset 0px 2px 1px 0px rgba(255,255,255,0.22), inset 0px -2px 0px 0px rgba(14,56,125,0.18), 0px 2px 5px 0px rgba(25,33,61,0.17);
  // }
  &:hover {
    background-color: #79bbff;
    border: solid 1px #79bbff;
    color: #fff;
  }
}

.comp-button--success {
  color: #fff;
  background: #14D0AE;
  border-color: #14D0AE;
  &:hover {
    border: solid 1px #e9f2ff;
  }
}

.comp-button--warning {
  color: #fff;
  background: #e6a23c;
  border-color: #e6a23c;
  &:hover {
    background-color: #EEBEE7;
    border: solid 1px #EEBEE7;
  }
}
.comp-button--pink {
  color: #FF2D46;
  background-color: #FF2D461A;
  border: solid 1px #FF2D461A;
}
.comp-button--danger {
  color: #fff;
  background: #FF2D46;
  border-color: #FF2D46;
  &:hover {
    background-color: #fa6375;
    border: solid 1px #fa6375;
  }
}

.comp-button--info {
  color: #263445;
  font-size: 13px;
  font-weight: 500;
  background: #fff;
  background-color: #fff;
  border: solid 1px #DADFE4;
  &:not(.is-disabled) {
    &:hover {
      border: solid 1px #DADFE4;
      color: #2684FC;
    }
  }
}
.comp-button--delete {
  color: #FF2D46;
  background: #F9FAFB;
  border: solid 1px #F9FAFB;
}
.comp-button--text {
  color: #192434;
  padding: 0 0;
  background: transparent;
  border-color: transparent;
  &:hover {
    color: #2684FC !important;
  }
}
.comp-button--plain {
  color: #192434;
  background: transparent;
  border-color: transparent;
  padding: 8.5px 0;
  &:hover {
    color: #2684FC !important;
  }
}
</style>